import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';

import { useUser } from '~/common/hooks/useAuthentication';
import { useNavFeatureFlags } from '~/common/hooks/useNavFeatureFlags';
import { useSignalRIsConnected } from '~/stores/useSignalRStore';

// local imports
import Button from '../Button';
import {
  handlePrimaryButtonClick,
  handleSecondaryButtonClick,
} from './signalRNavigation';
import { Action, PrimaryAction } from './types';

const StyledFooter = styled.footer<any>`
  box-sizing: border-box;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 32px 0 32px;
  border-top: 1px solid ${(props) => props.theme.colors?.gray[100]};
  @media print {
    display: none;
  }
`;

type FooterProps = {
  primaryButton?: PrimaryAction;
  secondaryButton?: Action;
  verifiedRule?: {
    ruleResultId?: number;
    isUnderwriterVerified?: boolean;
  };
  error?: boolean | string;
  waitForSignalR: boolean;
  selectedCategory?: number;
  shouldAutoCheck?: boolean;
};

const Footer = ({
  primaryButton,
  secondaryButton,
  error,
  waitForSignalR,
  shouldAutoCheck = false,
}: Readonly<FooterProps>) => {
  const { selectedAlert, selectedCategory, shouldGetNextAlert } = useSelector(
    (state) => state.home,
  );
  const { isUnderwriter, canWriteUnderwriterCheckbox } = useUser();
  const { FEVerified } = useSelector((state) => state.feVerified);
  const enableActionBar = useNavFeatureFlags().IsActionBarEnabled;
  const dispatch = useDispatch();
  const isSignalRConnected = useSignalRIsConnected();

  if (enableActionBar) {
    return null;
  }

  return (
    <StyledFooter role="group">
      {secondaryButton && (
        <Button
          mr="1rem"
          isPrimary={false}
          onClick={(event) =>
            handleSecondaryButtonClick(event, secondaryButton?.onSubmit)
          }
          isDisabled={secondaryButton?.isDisabled || !!error}
          isLoading={secondaryButton?.isSubmitting}
          dataTestid={`secondaryButton-${secondaryButton?.name}`}
        >
          {secondaryButton?.name}
        </Button>
      )}

      {/* Continue Button - Primary */}
      {primaryButton &&
        (primaryButton.isFormSubmit ? (
          <Button
            type="submit"
            id={primaryButton?.id}
            isDisabled={primaryButton?.isDisabled || !!error}
            isLoading={primaryButton?.isSubmitting || shouldGetNextAlert}
          >
            {primaryButton?.name || 'Continue'}
          </Button>
        ) : (
          <Button
            id={primaryButton?.id}
            isDisabled={primaryButton?.isDisabled || !!error}
            isLoading={primaryButton?.isSubmitting || shouldGetNextAlert}
            onClick={(event) =>
              handlePrimaryButtonClick(
                event,
                primaryButton?.onSubmit,
                dispatch,
                selectedAlert,
                waitForSignalR,
                selectedCategory,
                shouldAutoCheck,
                FEVerified,
                canWriteUnderwriterCheckbox || isUnderwriter,
                isUnderwriter,
                isSignalRConnected,
              )
            }
          >
            {primaryButton?.name || 'Continue'}
          </Button>
        ))}
    </StyledFooter>
  );
};

export default Footer;
